import { Leaf, Droplets, Recycle, Shield, CheckCircle, Clock, Award, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { lazy, Suspense } from 'react'
import SEO from '../components/SEO'
import { serviceSEOData } from '../utils/serviceSEO'

// ✅ Import images to ensure they work in production
import hero1 from '../assets/Services/environmental/hero-1.jpg'
import hero2 from '../assets/Services/environmental/hero-2.jpg'
import hero3 from '../assets/Services/environmental/hero-3.jpg'
import hero4 from '../assets/Services/environmental/hero-4.jpg'
import wasteImg from '../assets/Services/environmental/waste-management.jpg'
import waterImg from '../assets/Services/environmental/water-treatment.jpg'
import airImg from '../assets/Services/environmental/air-quality.jpg'
import soilImg from '../assets/Services/environmental/soil-remediation.jpg'
import consultingImg from '../assets/Services/environmental/environmental-consulting.jpg'
import sustainabilityImg from '../assets/Services/environmental/sustainability.jpg'

const ServicePageLayout = lazy(() => import('../components/ServicePageLayout'))

const Environmental = () => {
  const { t } = useTranslation()

  const service = {
    heroSlides: [
      {
        image: hero1, // ✅ Use imported variable
        title: t('environmental.heroSlides.0.title'),
        subtitle: t('environmental.heroSlides.0.subtitle'),
        description: t('environmental.heroSlides.0.description')
      },
      {
        image: hero2,
        title: t('environmental.heroSlides.1.title'),
        subtitle: t('environmental.heroSlides.1.subtitle'),
        description: t('environmental.heroSlides.1.description')
      },
      {
        image: hero3,
        title: t('environmental.heroSlides.2.title'),
        subtitle: t('environmental.heroSlides.2.subtitle'),
        description: t('environmental.heroSlides.2.description')
      },
      {
        image: hero4,
        title: t('environmental.heroSlides.3.title'),
        subtitle: t('environmental.heroSlides.3.subtitle'),
        description: t('environmental.heroSlides.3.description')
      }
    ],
    introText: t('environmental.introText'),
    stats: [
      { value: "100%", label: t('environmental.stats.compliance') },
      { value: "24/7", label: t('environmental.stats.monitoring') },
      { value: t('environmental.stats.zero'), label: t('environmental.stats.wasteGoal') },
      { value: "ISO", label: t('environmental.stats.certified') }
    ],
    capabilities: t('environmental.capabilities', { returnObjects: true }),
    services: [
      {
        icon: Recycle,
        title: t('environmental.services.wasteManagement.title'),
        description: t('environmental.services.wasteManagement.description'),
        image: wasteImg // ✅ Use imported variable
      },
      {
        icon: Droplets,
        title: t('environmental.services.waterQuality.title'),
        description: t('environmental.services.waterQuality.description'),
        image: waterImg
      },
      {
        icon: Shield,
        title: t('environmental.services.airQuality.title'),
        description: t('environmental.services.airQuality.description'),
        image: airImg
      },
      {
        icon: Leaf,
        title: t('environmental.services.environmentalImpact.title'),
        description: t('environmental.services.environmentalImpact.description'),
        image: soilImg
      },
      {
        icon: CheckCircle,
        title: t('environmental.services.renewableEnergy.title'),
        description: t('environmental.services.renewableEnergy.description'),
        image: consultingImg
      },
      {
        icon: Award,
        title: t('environmental.services.greenBuilding.title'),
        description: t('environmental.services.greenBuilding.description'),
        image: sustainabilityImg
      }
    ],
    workflow: [
      {
        step: "01",
        title: t('environmental.workflow.0.title'),
        description: t('environmental.workflow.0.description')
      },
      {
        step: "02",
        title: t('environmental.workflow.1.title'),
        description: t('environmental.workflow.1.description')
      },
      {
        step: "03",
        title: t('environmental.workflow.2.title'),
        description: t('environmental.workflow.2.description')
      },
      {
        step: "04",
        title: t('environmental.workflow.3.title'),
        description: t('environmental.workflow.3.description')
      }
    ]
  }

  return (
    <>
      <SEO {...serviceSEOData['environmental']} url="/services/environmental" />
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
        <ServicePageLayout
        title={service.heroSlides[0].title} // Fallback
        subtitle={service.heroSlides[0].subtitle} // Fallback
        description={service.heroSlides[0].description} // Fallback
        backgroundImages={service.heroSlides.map(slide => slide.image)}
        heroSlides={service.heroSlides}
        introText={service.introText}
        capabilities={service.capabilities}
        services={service.services}
        stats={service.stats}
        workflow={service.workflow}
        />
      </Suspense>
    </>
  )
}

export default Environmental